import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { products } from '../mock';
import { ArrowLeft, ArrowUpRight, Check, Circle, Zap, ShieldCheck, Map, Layers, Sparkles, Download, Mail } from 'lucide-react';
import { CircuitAccent, TopoLines, LayeredMountains } from '../components/graphics/Decor';
import usePageMeta from '../hooks/usePageMeta';
import { EditionsSection } from '../components/ProductEditions';
import { ProductFAQ } from '../components/ProductFAQ';
const detail = {
  axiom: {
    subtitle: 'AI-powered underwriting guidance for title professionals — in any state, built on deep Washington roots.',
    heroKicker: 'AXIOM — Virtual Underwriter',
    video: '/videos/axiom-intro.mp4',
    videoPoster: '/videos/axiom-poster.jpg',
    screenshot: '/screenshots/axiom.jpg',
    screenshotCaption: 'Watch AXIOM answer a real underwriting question, start to finish.',
    editions: [
      {
        id: 'underwriter',
        name: 'AXIOM for Underwriters',
        shortName: 'for Underwriters',
        logo: '/brand-art/axiom-underwriter-mark.png',
        audience: 'For underwriters & title professionals',
        url: 'https://underwriter.nwta.app/',
        tagline:
          'Underwriting-grade answers in any state — the requirements to insure, written for the professional who has to clear the file.',
        points: [
          'Ask in any state, get the requirements to insure — sourced correctly the first time',
          'Escalate to counsel-grade or claims-grade depth in one click',
          'Your bulletins, notices, and proprietary guidelines — searchable on demand',
        ],
      },
      {
        id: 'lenders',
        name: 'AXIOM for Lenders',
        shortName: 'for Lenders',
        logo: '/brand-art/axiom-lenders-mark.png',
        audience: 'For lenders & loan risk teams',
        url: 'https://lenders.nwta.app/',
        tagline:
          'Instant, underwriting-grade answers on title risk and collateral exposure — every response grounded in real ALTA endorsement language.',
        points: [
          'Ask, don’t search — lending-risk answers in seconds, not a 40-page manual',
          'Cited, not guessed — grounded in actual ALTA endorsement text',
          'Upload a title report or loan doc and get flagged risk items instantly',
          'Draft client-ready emails for borrowers, sellers, agents, and attorneys',
        ],
      },
      {
        id: 'agents',
        name: 'AXIOM for Agents',
        shortName: 'for Agents',
        logo: '/favicon-192.png',
        audience: 'For title agents nationwide',
        url: 'https://axiom.northwesttitlealliance.com/',
        tagline:
          'The same underwriting brain, delivered under Northwest Tech Alliance branding for agents beyond the Northwest.',
        points: [
          'Underwriting-grade answers for your agency desk — in any state',
          'The requirements to insure, sourced correctly the first time',
          'Escalate to counsel-grade or claims-grade depth when a file calls for it',
          'Not a generic chatbot — built on how title professionals assess risk',
        ],
      },
      {
        id: 'attorney',
        name: 'AXIOM for Attorneys',
        shortName: 'for Attorneys',
        logo: '/brand-art/axiom-attorneys-mark.png',
        audience: 'For real estate & title attorneys',
        url: 'https://attorney.nwta.app/',
        tagline:
          'Focused, heavily-cited answers built for the attorney’s desk — litigation, claims, research, and title-underwriting analysis, in one surface.',
        points: [
          'Litigation-ready analysis, either side of the “v.” — elements, defenses, and proof burdens',
          'Claims work, pursuing or defending — coverage drivers and what to document',
          'Neutral, exhaustive research memos — full cited analysis, edge cases, open questions',
          'Underwriting (VU): what a title insurer would actually insure — decision-first framing',
        ],
      },
    ],
    upcomingEditions: [
      {
        id: 'national-agency',
        name: 'AXIOM for National Agents',
        logo: '/brand-art/axiom-national-agents-mark.png',
        audience: 'For multi-state agency operations',
        tagline: 'Consistent, state-specific underwriting answers across every market you operate in.',
      },
      {
        id: 'commercial',
        name: 'AXIOM for Commercial',
        logo: '/brand-art/axiom-commercial-mark.png',
        audience: 'For commercial title teams',
        tagline: 'Underwriting-grade answers tuned for complex commercial transactions and endorsements.',
      },
    ],
    features: [
      { icon: Map, title: 'Any state, on demand', body: 'State-specific underwriting requirements across the country — sourced correctly the first time.' },
      { icon: Zap, title: 'Instant answers', body: 'Ask a nuanced underwriting question and get a clear, authoritative response in seconds.' },
      { icon: ShieldCheck, title: 'Built for confidence', body: 'Explains its reasoning, cites relevant context, and stays within a defined scope.' },
      { icon: Sparkles, title: 'Always improving', body: 'Continuously refined by working title professionals — not a generic chatbot.' },
    ],
    resource: {
      kicker: 'New — AXIOM for Outlook',
      title: 'Ask AXIOM, right inside your inbox.',
      body: 'Draft citation-backed email replies without leaving Outlook — AXIOM drafts, you review and send. Free for AXIOM customers, with a 5-minute setup. Download the flyer for install steps.',
      file: '/downloads/ask-axiom-outlook-flyer.pdf',
      fileLabel: 'Download the Outlook Add-in Flyer',
    },
    useCases: [
      'Complex vesting and chain-of-title questions',
      'Judgments, liens, and priority disputes',
      'Easement and encumbrance interpretation',
      'HOA, CC&R, and plat review nuance',
    ],
    faqs: [
      {
        q: 'What editions of AXIOM are available?',
        a: 'Four editions are live today: AXIOM for Underwriters, AXIOM for Lenders, AXIOM for Agents, and AXIOM for Attorneys. Two more are coming soon: AXIOM for National Agents and AXIOM for Commercial.',
      },
      {
        q: 'What does AXIOM for Lenders do?',
        a: 'AXIOM for Lenders gives instant, underwriting-grade answers on title risk and collateral exposure, grounded in real ALTA endorsement language. Lenders can upload a title report or loan document to get flagged risk items instantly, and draft client-ready emails for borrowers, sellers, agents, and attorneys.',
      },
      {
        q: 'Does AXIOM work outside Washington State?',
        a: 'Yes. AXIOM provides state-specific underwriting guidance in any U.S. state — the requirements to insure, sourced correctly the first time.',
      },
      {
        q: 'Is AXIOM just a generic AI chatbot?',
        a: 'No. AXIOM is built on how title professionals assess risk. Its answers are cited, not guessed, and users can escalate to counsel-grade or claims-grade depth in one click.',
      },
      {
        q: 'How do I get an AXIOM account?',
        a: 'Call the sales line at (253) 453-1754 or email sales@northwesttitlealliance.com and the team will set up your workspace.',
      },
    ],
  },
  art: {
    subtitle: 'An OIC-compliant, AI-rendered title plant — back to State Patent — built for the modern Washington title professional.',
    heroKicker: 'ART — AI Rendered Title',
    video: '/videos/art-demo.mp4',
    videoPoster: '/videos/art-poster.jpg',
    screenshot: '/screenshots/art.jpg',
    screenshotCaption: 'Watch ART build a complete, verified title exam — address in, commitment out.',
    heroArt: '/brand-art/art-tree.png',
    partner: {
      name: 'TitleCat',
      url: 'https://titlecat.com',
      kicker: 'Licensed technology partner',
      headline: 'Powered by TitleCat — the AI-native workbench for title examiners.',
      body: 'ART is built on TitleCat technology under license, tuned by Northwest Title Alliance for Washington State practice and OIC compliance. Address in, exam out: the engine searches county recorders, assessors, title plants, courts and PACER, reads and indexes every document it finds, and links every finding to its source.',
      points: [
        'Fully auditable — no black box; every finding links to its source document',
        'AI reads deeds, liens, easements, plats and more — examiners keep the final say',
        'Commitment-ready output in hours, not days',
      ],
    },
    features: [
      { icon: ShieldCheck, title: 'OIC-compliant', body: 'Built to Washington State regulatory standards from day one.' },
      { icon: Layers, title: 'Back to State Patent', body: 'A complete chain, indexed and searchable, all the way back to the original patent.' },
      { icon: Zap, title: 'Fast, accurate searches', body: 'AI-assisted parsing and matching that reduces the manual burden without cutting corners.' },
      { icon: Map, title: 'County-by-county rollout', body: 'Live in Jefferson, Mason, Kitsap, Clallam, Grays Harbor, Pierce & Thurston today — more coming across all 39 counties.' },
    ],
    useCases: [
      'Full title searches without a legacy plant',
      'Rapid preliminary reports',
      'Chain and document validation',
      'New-market entry without infrastructure',
    ],
    faqs: [
      {
        q: 'What is ART (AI Rendered Title)?',
        a: 'ART is an OIC-compliant, AI-powered title plant with records back to State Patent — fundamentally changing how title searches are performed in Washington State. Address in, exam out.',
      },
      {
        q: 'What does OIC-compliant mean?',
        a: 'ART is built to the regulatory standards of the Washington State Office of the Insurance Commissioner (OIC) for title plants, from day one.',
      },
      {
        q: 'How far back do ART’s records go?',
        a: 'All the way back to State Patent — a complete chain of title, indexed and searchable.',
      },
      {
        q: 'Which counties is ART available in?',
        a: 'ART is live in seven counties today — Jefferson, Mason, Kitsap, Clallam, Grays Harbor, Pierce, and Thurston — with a county-by-county rollout planned across all 39 Washington counties.',
      },
      {
        q: 'Is ART a black box?',
        a: 'No. ART is fully auditable — every finding links to its source document, and examiners keep the final say.',
      },
    ],
  },
  truenorth: {
    subtitle: 'ALTA/NSPS survey underwriting intelligence — drop in a title commitment, get closure checks, plottability analysis, and cited curative guidance.',
    heroKicker: 'TrueNorth — Survey Intelligence',
    heroArt: '/brand-art/truenorth-mark.png',
    heroArtClass: 'top-24 w-[340px] xl:w-[400px]',
    features: [
      { icon: Map, title: 'Does the legal close?', body: 'Automated metes-and-bounds closure checks with a plotted diagram — not a manual recheck.' },
      { icon: Layers, title: 'B-II plottability & Table A', body: 'Every survey exception assessed against the 2021 ALTA/NSPS Table A items.' },
      { icon: ShieldCheck, title: 'Legal-vs-vesting cross-check', body: 'Catch discrepancies between the recorded legal description and vesting deed before they become a claim.' },
      { icon: Sparkles, title: 'Grounded, cited guidance', body: 'Curative paths and citations for every requirement and exception — explanations only, never a substitute for counsel.' },
    ],
    useCases: [
      'Survey review on commercial and residential files',
      'Clearing plottable vs blanket exceptions',
      'Extended coverage and ALTA policy prep',
      'Catching legal/vesting discrepancies pre-closing',
    ],
    faqs: [
      {
        q: 'What is TrueNorth?',
        a: 'TrueNorth is ALTA/NSPS survey underwriting intelligence. Upload a title commitment PDF and it runs automated legal description closure checks, assesses every Schedule B-II exception against the 2021 ALTA/NSPS Table A items, and cross-checks the legal description against the vesting deed.',
      },
      {
        q: 'Does TrueNorth check whether the legal description closes?',
        a: 'Yes. TrueNorth performs automated metes-and-bounds closure checks and produces a plotted diagram — no manual recheck required.',
      },
      {
        q: 'Which states does TrueNorth cover?',
        a: 'TrueNorth works nationwide — all 50 states and D.C., with auto-detection of the governing state from your commitment (a few states have limited coverage).',
      },
      {
        q: 'Is TrueNorth legal advice?',
        a: 'No. TrueNorth provides grounded, cited explanations and curative paths to inform professional judgment — it never substitutes for counsel.',
      },
    ],
  },
};

const launchUrls = {
  axiom: 'https://underwriter.nwta.app/',
  art: 'https://art.titlecat.com/',
  truenorth: 'https://truenorth.nwta.app/',
};

const LaunchButton = ({ productId, name }) => (
  <a
    href={launchUrls[productId]}
    target="_blank"
    rel="noreferrer"
    className="group inline-flex items-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[14.5px] font-medium px-6 py-3.5 transition-all shadow-md hover:shadow-lg"
  >
    Launch {name} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const EditionButtons = ({ editions, idPrefix }) => (
  <>
    {editions.map((e) => (
      <a
        key={e.id}
        href={e.url}
        target="_blank"
        rel="noreferrer"
        className="group inline-flex items-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[14.5px] font-medium px-6 py-3.5 transition-all shadow-md hover:shadow-lg"
        data-testid={`${idPrefix}-launch-${e.id}-btn`}
      >
        {e.name} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    ))}
  </>
);

const ProductActions = ({ product, d, productId, idPrefix }) =>
  d.editions ? <EditionButtons editions={d.editions} idPrefix={idPrefix} /> : <LaunchButton productId={productId} name={product.name} />;

const RequestInfoLink = () => (
  <Link
    to="/#contact"
    className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-[#0B2545] text-[14.5px] font-medium px-6 py-3.5 transition-colors"
  >
    Request Information
  </Link>
);

const NotFound = () => (
  <div className="min-h-screen bg-[#F6F8FA]">
    <Header />
    <div className="max-w-2xl mx-auto px-5 py-32 text-center">
      <h1 className="font-serif text-4xl text-[#0B2545]">Product not found</h1>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 text-[#3E5C76] hover:text-[#0B2545]">
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>
    </div>
    <Footer />
  </div>
);

const ProductHero = ({ product, d, productId }) => (
  <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
    <div className="absolute inset-0 topo-bg pointer-events-none" />
    <TopoLines className="absolute top-16 left-0 w-full h-60 pointer-events-none" opacity={0.35} />
    {!d.heroArt && <CircuitAccent className="hidden md:block absolute top-24 right-8 w-56 opacity-70 pointer-events-none" />}
    <div className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#B8C7D6]/45 to-transparent blur-3xl pointer-events-none animate-float" />
    <LayeredMountains className="absolute bottom-0 left-0 right-0 w-full h-56 md:h-64 pointer-events-none opacity-90" />
    {d.heroArt && (
      <img
        src={d.heroArt}
        alt={`${product.name} logo artwork`}
        className={`hidden lg:block absolute right-10 mix-blend-multiply opacity-95 pointer-events-none ${
          d.heroArtClass || 'top-32 w-[400px] xl:w-[460px]'
        }`}
        data-testid="product-hero-art"
      />
    )}

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-[#0B2545] transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to overview
      </Link>
      <div className="mt-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-300/70 bg-white/70 backdrop-blur-sm px-3 py-1.5 text-[12px] font-medium text-slate-600 shadow-sm" data-testid="product-status-pill">
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <Circle className="w-1.5 h-1.5 fill-emerald-500 text-emerald-500" />
            {product.status}
          </span>
          <span className="text-slate-400">•</span>
          {d.heroKicker}
        </div>
      </div>
      <h1 className="mt-6 font-serif text-[46px] leading-[1.04] sm:text-[64px] md:text-[80px] font-medium tracking-[-0.02em] text-[#0B2545]">
        {product.name}
        <span className="block text-slate-500 text-[28px] sm:text-[36px] md:text-[42px] font-normal mt-2">
          {product.tagline}
        </span>
      </h1>
      <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-slate-600">{d.subtitle}</p>
      <div className="mt-9 flex flex-wrap items-center gap-3">
        <ProductActions product={product} d={d} productId={productId} idPrefix="hero" />
        <RequestInfoLink />
      </div>
    </div>
  </section>
);

const ProductPreview = ({ product, d, productId }) => (
  <section className="relative pb-4 -mt-6 md:-mt-10">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="relative rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden" data-testid="product-screenshot">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-[#F6F8FA]">
          <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
          <span className="ml-3 text-[11.5px] text-slate-400 font-medium truncate">{launchUrls[productId]}</span>
        </div>
        {d.video ? (
          <video
            src={d.video}
            poster={d.videoPoster}
            controls
            playsInline
            preload="metadata"
            className="w-full h-auto block bg-[#06121F]"
            data-testid="product-demo-video"
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={d.screenshot}
            alt={`${product.name} interface preview`}
            className="w-full h-auto block"
            loading="lazy"
          />
        )}
      </div>
      <p className="mt-4 text-center text-[13.5px] text-slate-500 italic">{d.screenshotCaption}</p>
    </div>
  </section>
);

const FeatureGrid = ({ features }) => (
  <section className="relative py-20 bg-white">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">What it does</div>
      <h2 className="mt-3 font-serif text-3xl md:text-4xl font-medium tracking-[-0.02em] text-[#0B2545] max-w-2xl">
        Purpose-built capability, not a generic tool.
      </h2>
      <div className="mt-12 grid md:grid-cols-2 gap-5">
        {features.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-[#F1F4F8] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#0B2545]" />
              </div>
              <h3 className="mt-4 font-serif text-xl font-semibold text-[#0B2545]">{f.title}</h3>
              <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{f.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const PartnerSection = ({ partner }) => (
  <section className="relative py-16 bg-white" data-testid="partner-section">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F6F8FA] to-white p-8 md:p-10 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">{partner.kicker}</div>
          <h2 className="mt-3 font-serif text-2xl md:text-3xl font-medium tracking-[-0.02em] text-[#0B2545]">
            {partner.headline}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{partner.body}</p>
          <a
            href={partner.url}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#3E5C76] hover:text-[#0B2545] transition-colors"
            data-testid="partner-link"
          >
            Learn more about {partner.name} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <ul className="space-y-3">
          {partner.points.map((pt) => (
            <li key={pt} className="flex items-start gap-3 rounded-xl bg-white border border-slate-200 px-5 py-4">
              <div className="mt-0.5 w-6 h-6 rounded-md bg-[#3E5C76] flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-[14.5px] text-slate-700 leading-relaxed">{pt}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const ResourceSection = ({ resource }) => (
  <section className="relative py-16 bg-white" data-testid="resource-section">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0B2545] to-[#13315C] p-8 md:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-[#B8C7D6]">
            <Mail className="w-4 h-4" /> {resource.kicker}
          </div>
          <h2 className="mt-3 font-serif text-2xl md:text-3xl font-medium tracking-[-0.02em] text-white">
            {resource.title}
          </h2>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-300">{resource.body}</p>
        </div>
        <a
          href={resource.file}
          download
          className="group inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-100 text-[#0B2545] text-[14.5px] font-semibold px-6 py-3.5 transition-all shadow-md hover:shadow-lg"
          data-testid="resource-download-btn"
        >
          <Download className="w-4 h-4" /> {resource.fileLabel}
        </a>
      </div>
    </div>
  </section>
);

const UseCases = ({ name, useCases }) => (
  <section className="relative py-20 bg-[#F1F4F8]">
    <div className="absolute inset-0 topo-bg opacity-60 pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">How teams use it</div>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl font-medium tracking-[-0.02em] text-[#0B2545]">
          Everyday moments where {name} pays for itself.
        </h2>
      </div>
      <ul className="space-y-3">
        {useCases.map((u) => (
          <li key={u} className="flex items-start gap-3 rounded-xl bg-white border border-slate-200 px-5 py-4">
            <div className="mt-0.5 w-6 h-6 rounded-md bg-[#0B2545] flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-[15px] text-slate-700 leading-relaxed">{u}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const ProductCTA = ({ product, d, productId }) => (
  <section className="relative py-20 bg-white">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-[-0.02em] text-[#0B2545]">
        Ready to try {product.name}?
      </h2>
      <p className="mt-4 text-[16.5px] text-slate-600 max-w-xl mx-auto">
        Talk to us about a walkthrough tailored to your operation — or launch it now and take it for a spin.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <ProductActions product={product} d={d} productId={productId} idPrefix="cta" />
        <RequestInfoLink />
      </div>
    </div>
  </section>
);

const pageMeta = (product, d, productId) => {
  if (!product || !d) return ['Product Not Found | Northwest Title Alliance'];
  return [
    `${product.name} — ${product.tagline} | Northwest Title Alliance`,
    d.subtitle,
    `https://northwesttitlealliance.com/product/${productId}`,
    `https://northwesttitlealliance.com/og-${productId}.png`,
  ];
};

const ProductSections = ({ product, d, productId }) => (
  <>
    {(d.video || d.screenshot) && <ProductPreview product={product} d={d} productId={productId} />}
    {d.editions && <EditionsSection editions={d.editions} upcomingEditions={d.upcomingEditions} />}
    <FeatureGrid features={d.features} />
    {d.resource && <ResourceSection resource={d.resource} />}
    {d.partner && <PartnerSection partner={d.partner} />}
    <UseCases name={product.name} useCases={d.useCases} />
    {d.faqs && <ProductFAQ name={product.name} faqs={d.faqs} />}
  </>
);

export default function ProductPage() {
  const { productId } = useParams();
  const product = products.find((p) => p.id === productId);
  const d = detail[productId];
  const [title, subtitle, ogUrl, ogImage] = pageMeta(product, d, productId);
  usePageMeta(title, subtitle, ogUrl, ogImage);

  if (!product || !d) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#F6F8FA]">
      <Header />
      <ProductHero product={product} d={d} productId={productId} />
      <ProductSections product={product} d={d} productId={productId} />
      <ProductCTA product={product} d={d} productId={productId} />
      <Footer />
    </div>
  );
}
